import Axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@/common/config/runtime.ts';
import { ApiError } from '@/common/errors/api-error.ts';

export const AXIOS_INSTANCE = Axios.create({ baseURL: API_BASE_URL });

AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const method = error.config?.method?.toUpperCase();
    const path = error.config?.url;
    const requestId =
      (error.response?.headers?.['x-request-id'] as string | undefined) ??
      (error.response?.headers?.['x-correlation-id'] as string | undefined);

    if (error.code === 'ERR_CANCELED') {
      throw new ApiError({
        message: 'Request was canceled',
        kind: 'abort',
        status,
        method,
        path,
        requestId,
        cause: error,
      });
    }

    if (!error.response) {
      throw new ApiError({
        message: error.message || 'Network request failed',
        kind: 'network',
        method,
        path,
        cause: error,
      });
    }

    const message =
      typeof (error.response.data as { message?: unknown } | undefined)?.message ===
      'string'
        ? ((error.response.data as { message?: string }).message ?? 'Request failed')
        : (error.message ?? 'Request failed');

    throw new ApiError({
      message,
      kind:
        status === 401
          ? 'auth'
          : status === 403
            ? 'permission'
            : status === 404
              ? 'notFound'
              : status === 400 || status === 422
                ? 'validation'
                : typeof status === 'number' && status >= 500
                  ? 'server'
                  : 'unknown',
      status,
      method,
      path,
      requestId,
      cause: error,
    });
  },
);

// Add a second `options` argument to pass extra options to each query
export const axios = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const promise = AXIOS_INSTANCE({
    paramsSerializer: {
      indexes: null,
    },
    withCredentials: true,
    ...config,
    ...options,
  }).then(({ data }) => data);

  return promise;
};

// Override the return error type for react-query and swr
export type ErrorType<Error> = AxiosError<Error>;

// Standard body type
export type BodyType<BodyData> = BodyData;

// Or wrap the body type if processing data before sending
// export type BodyType<BodyData> = CamelCase<BodyData>;
